import { useState } from 'react';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { roleName } from '../../lib/approvals.ts';
import { dateTime, day, money, parseMoney } from '../../lib/format.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';

type Op = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'set' | 'not_set';
interface FieldInfo {
  key: string;
  label: string;
  type: string;
  values: string[] | null;
}
interface Condition {
  field: string;
  op: Op;
  value: string | number | boolean | null;
}
type RuleAction = { kind: 'todo'; title: string; user: string | null; role: string | null; due_days: number; notes: string | null } | { kind: 'assign'; field: string; user: string };
interface Rule {
  id: string;
  name: string;
  active: boolean;
  trigger: 'event' | 'date';
  event: string | null;
  model: string;
  date_field: string | null;
  offset_days: number;
  conditions: Condition[];
  actions: RuleAction[];
  runs: number;
  last_run_at: string | null;
  failed_week: number;
}
interface Catalog {
  triggers: { event: string; model: string; fields: FieldInfo[] }[];
  dates: { model: string; field: string; fields: FieldInfo[] }[];
  assignable: Record<string, { key: string; label: string }[]>;
  todo: boolean;
  people: { id: string; name: string }[];
  roles: string[];
}
interface Run {
  id: string;
  label: string;
  ok: boolean;
  done: string[];
  error: string | null;
  at: string;
}

/** What sets a rule off, in words. */
const EVENT_LABEL: Record<string, MessageKey> = {
  'invoicing.invoice.issued': 'au.ev_invoice_issued',
  'invoicing.payment.created': 'au.ev_payment',
  'invoicing.order.confirmed': 'au.ev_order_confirmed',
  'invoicing.delivery.done': 'au.ev_delivered',
  'purchase.order.confirmed': 'au.ev_po_sent',
  'purchase.bill.created': 'au.ev_bill',
  'inventory.item.low': 'au.ev_low_stock',
  'crm.lead.created': 'au.ev_lead',
  'sales.order.paid': 'au.ev_sale_paid',
  'appointments.booked': 'au.ev_booked',
  'hr.leave.requested': 'au.ev_leave',
  'hr.applicant.new': 'au.ev_applicant',
  'mrp.order.done': 'au.ev_mo_done',
};
const DATE_LABEL: Record<string, MessageKey> = {
  'invoicing.invoice.due_date': 'au.dt_invoice_due',
  'purchase.bill.due_date': 'au.dt_bill_due',
  'project.task.deadline': 'au.dt_task_deadline',
  'invoicing.quote.valid_until': 'au.dt_quote_until',
};
const OP_LABEL: Record<Op, MessageKey> = {
  eq: 'au.op_eq',
  ne: 'au.op_ne',
  gt: 'au.op_gt',
  gte: 'au.op_gte',
  lt: 'au.op_lt',
  lte: 'au.op_lte',
  contains: 'au.op_contains',
  set: 'au.op_set',
  not_set: 'au.op_not_set',
};
/** Field types a person can sensibly compare on this screen; record links and amounts of stock stay out. */
const USABLE = new Set(['string', 'text', 'int', 'bool', 'money', 'date', 'datetime', 'enum', 'user']);
const opsFor = (type: string): Op[] =>
  type === 'bool' || type === 'enum' || type === 'user'
    ? ['eq', 'ne', 'set', 'not_set']
    : type === 'string' || type === 'text'
      ? ['eq', 'ne', 'contains', 'set', 'not_set']
      : ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'set', 'not_set'];
const enumText = (v: string) => v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ');

const triggerText = (r: Pick<Rule, 'trigger' | 'event' | 'model' | 'date_field' | 'offset_days'>) => {
  if (r.trigger === 'event') return t(EVENT_LABEL[r.event ?? ''] ?? 'au.ev_unknown');
  const what = t(DATE_LABEL[`${r.model}.${r.date_field}`] ?? 'au.ev_unknown');
  const n = Math.abs(r.offset_days);
  return r.offset_days === 0 ? t('au.on_the_day', { what }) : r.offset_days < 0 ? tn('au.days_before', n, { what }) : tn('au.days_after', n, { what });
};

function valueText(c: Condition, f: FieldInfo | undefined, people: Catalog['people']) {
  if (c.op === 'set' || c.op === 'not_set') return '';
  if (f?.type === 'money' && typeof c.value === 'number') return money(c.value);
  if (f?.type === 'bool') return c.value === true || c.value === 'true' ? t('au.yes') : t('au.no');
  if (f?.type === 'user') return people.find((p) => p.id === c.value)?.name ?? '—';
  if (f?.type === 'date' && typeof c.value === 'string' && c.value) return day(c.value);
  if (f?.type === 'enum' && typeof c.value === 'string') return enumText(c.value);
  return String(c.value ?? '');
}
const conditionText = (c: Condition, fields: FieldInfo[], people: Catalog['people']) => {
  const f = fields.find((x) => x.key === c.field);
  return [f?.label ?? c.field, t(OP_LABEL[c.op]), valueText(c, f, people)].filter(Boolean).join(' ');
};
const actionText = (a: RuleAction, cat: Catalog, model: string) => {
  const who = (id: string | null) => cat.people.find((p) => p.id === id)?.name ?? '—';
  if (a.kind === 'todo') return t('au.do_todo_for', { title: a.title || '…', who: a.user ? who(a.user) : t('au.everyone_role', { role: roleName(a.role ?? '') }) });
  const field = cat.assignable[model]?.find((f) => f.key === a.field)?.label ?? a.field;
  return t('au.do_assign_to', { field, who: who(a.user) });
};
const fieldsOf = (cat: Catalog, r: Pick<Rule, 'trigger' | 'event' | 'model'>) =>
  ((r.trigger === 'event' ? cat.triggers.find((x) => x.event === r.event)?.fields : cat.dates.find((x) => x.model === r.model)?.fields) ?? []).filter((f) => USABLE.has(f.type));

/** Starting points, so a first rule takes a minute: the kinds of rules most businesses want. */
interface Recipe {
  key: string;
  icon: IconName;
  title: MessageKey;
  body: MessageKey;
  make: (cat: Catalog) => Draft | null;
}
const RECIPES: Recipe[] = [
  {
    key: 'big-invoice',
    icon: 'invoice',
    title: 'au.r_big_invoice',
    body: 'au.r_big_invoice_body',
    make: (cat) =>
      cat.triggers.some((x) => x.event === 'invoicing.invoice.issued') && cat.todo
        ? {
            name: t('au.r_big_invoice'),
            trigger: 'event',
            event: 'invoicing.invoice.issued',
            model: 'invoicing.invoice',
            date_field: null,
            offset_days: 0,
            conditions: [{ field: 'total', op: 'gt', value: 50_000_000 }],
            actions: [{ kind: 'todo', title: t('au.r_big_invoice_todo'), user: null, role: cat.roles.includes('manager') ? 'manager' : (cat.roles[0] ?? null), due_days: 1, notes: null }],
          }
        : null,
  },
  {
    key: 'due-soon',
    icon: 'clock',
    title: 'au.r_due_soon',
    body: 'au.r_due_soon_body',
    make: (cat) =>
      cat.dates.some((x) => x.model === 'invoicing.invoice') && cat.todo
        ? {
            name: t('au.r_due_soon'),
            trigger: 'date',
            event: null,
            model: 'invoicing.invoice',
            date_field: 'due_date',
            offset_days: -2,
            conditions: [{ field: 'state', op: 'eq', value: 'issued' }],
            actions: [{ kind: 'todo', title: t('au.r_due_soon_todo'), user: null, role: cat.roles.includes('accountant') ? 'accountant' : (cat.roles[0] ?? null), due_days: 0, notes: null }],
          }
        : null,
  },
  {
    key: 'web-leads',
    icon: 'users',
    title: 'au.r_web_leads',
    body: 'au.r_web_leads_body',
    make: (cat) =>
      cat.triggers.some((x) => x.event === 'crm.lead.created') && cat.assignable['crm.lead']?.some((f) => f.key === 'owner')
        ? {
            name: t('au.r_web_leads'),
            trigger: 'event',
            event: 'crm.lead.created',
            model: 'crm.lead',
            date_field: null,
            offset_days: 0,
            conditions: [{ field: 'source', op: 'eq', value: 'web' }],
            actions: [{ kind: 'assign', field: 'owner', user: cat.people[0]?.id ?? '' }],
          }
        : null,
  },
];

type Draft = Omit<Rule, 'id' | 'active' | 'runs' | 'last_run_at' | 'failed_week'> & { id?: string; active?: boolean };

/**
 * Settings → Automations: rules that do routine work on their own. "When an invoice over
 * 500,000 L is issued, give the manager a to-do to call the customer." Each rule says when, only
 * if, and what to do, and shows its history so nobody wonders whether it ran.
 */
export function Automations() {
  const { data: rules, reload } = useAction<Rule[]>('studio.automations');
  const { data: cat } = useAction<Catalog>('studio.automation_catalog');
  const [editing, setEditing] = useState<Draft | null>(null);
  const [history, setHistory] = useState<Rule | null>(null);
  const { busy, run } = useSave();
  const hasDate = rules?.some((r) => r.trigger === 'date' && r.active);

  const toggle = (r: Rule, active: boolean) =>
    void run(
      async () => {
        await action('studio.set_automation_active', { id: r.id, active });
        await reload();
      },
      active ? t('au.turned_on') : t('au.turned_off'),
    );
  const runDates = () =>
    void run(async () => {
      const { fired } = await action<{ fired: number }>('studio.run_date_automations');
      await reload();
      return fired;
    }, t('au.dates_checked'));
  const blank = (): Draft => ({
    name: '',
    trigger: 'event',
    event: cat?.triggers[0]?.event ?? null,
    model: cat?.triggers[0]?.model ?? '',
    date_field: null,
    offset_days: 0,
    conditions: [],
    actions: [],
  });
  const recipes = cat ? RECIPES.map((r) => ({ ...r, draft: r.make(cat) })).filter((r) => r.draft) : [];

  return (
    <ManageLayout
      title={t('manage.automations')}
      subtitle={t('au.subtitle')}
      actions={
        <>
          {hasDate ? (
            <Button variant="ghost" icon="calendar" onClick={runDates} busy={busy}>
              {t('au.run_dates')}
            </Button>
          ) : null}
          <Button variant="primary" icon="plus" onClick={() => setEditing(blank())} disabled={!cat}>
            {t('au.new_rule')}
          </Button>
        </>
      }
    >
      {!rules || !cat ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <>
          {rules.length ? (
            <div className="list-card au-list" role="list" aria-label={t('manage.automations')}>
              {rules.map((r) => {
                const fields = fieldsOf(cat, r);
                return (
                  <div key={r.id} role="listitem" className={`list-row au-row${r.active ? '' : ' archived'}`}>
                    <span className={`list-icon au-kind ${r.trigger}`} aria-hidden="true">
                      <Icon name={r.trigger === 'date' ? 'calendar' : 'repeat'} size={18} />
                    </span>
                    <button className="list-main" onClick={() => setEditing({ ...r })}>
                      <span className="list-title">{r.name}</span>
                      <span className="au-sentence">
                        <b>{t('au.when')}</b> {triggerText(r)}
                        {r.conditions.length ? (
                          <>
                            {' '}
                            <b>{t('au.and_if')}</b> {r.conditions.map((c) => conditionText(c, fields, cat.people)).join(` ${t('au.and')} `)}
                          </>
                        ) : null}
                        {' → '}
                        {r.actions.map((a) => actionText(a, cat, r.model)).join('; ')}
                      </span>
                    </button>
                    <span className="au-end">
                      {r.failed_week ? <StatusBadge tone="danger" icon="alert" label={tn('au.failed_week', r.failed_week)} /> : null}
                      <button className="au-runs" onClick={() => setHistory(r)}>
                        <span className="tabular">{tn('au.runs', r.runs)}</span>
                        <span className="muted small">{r.last_run_at ? t('au.last_run', { at: dateTime(r.last_run_at) }) : t('au.never_run')}</span>
                      </button>
                      <Switch checked={r.active} label={r.active ? t('au.turn_off_named', { name: r.name }) : t('au.turn_on_named', { name: r.name })} onChange={(v) => toggle(r, v)} disabled={busy} />
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon="repeat" title={t('au.empty')} body={t('au.empty_body')} />
          )}
          {recipes.length ? (
            <section className="au-recipes" aria-labelledby="au-recipes">
              <h2 className="section-title" id="au-recipes">
                {rules.length ? t('au.more_ideas') : t('au.start_with')}
              </h2>
              <div className="au-recipe-grid">
                {recipes.map((r) => (
                  <button key={r.key} className="au-recipe" onClick={() => setEditing(r.draft!)}>
                    <span className="au-recipe-icon" aria-hidden="true">
                      <Icon name={r.icon} size={18} />
                    </span>
                    <span className="au-recipe-title">{t(r.title)}</span>
                    <span className="muted small">{t(r.body)}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
      {editing && cat ? (
        <RuleDialog
          draft={editing}
          cat={cat}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}
      {history ? <HistoryDialog rule={history} onClose={() => setHistory(null)} /> : null}
    </ManageLayout>
  );
}

function RuleDialog({ draft, cat, onClose, onSaved }: { draft: Draft; cat: Catalog; onClose: () => void; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [d, setD] = useState<Draft>(draft);
  const [tried, setTried] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const fields = fieldsOf(cat, d);
  const assignable = cat.assignable[d.model] ?? [];
  const errors = {
    name: d.name.trim() ? null : t('form.required'),
    actions: d.actions.length ? null : t('au.need_action'),
    todo: d.actions.some((a) => (a.kind === 'todo' ? !a.title.trim() || (!a.user && !a.role) : !a.user)) ? t('au.action_incomplete') : null,
  };
  const invalid = Object.values(errors).some(Boolean);

  const pickTrigger = (value: string) => {
    if (value.startsWith('date:')) {
      const [model, field] = value.slice(5).split('|') as [string, string];
      set({ trigger: 'date', event: null, model, date_field: field, conditions: [], actions: d.actions.filter((a) => a.kind === 'todo') });
    } else {
      const tr = cat.triggers.find((x) => x.event === value)!;
      set({ trigger: 'event', event: value, model: tr.model, date_field: null, offset_days: 0, conditions: [], actions: d.actions.filter((a) => a.kind === 'todo') });
    }
  };
  const setCond = (i: number, patch: Partial<Condition>) => set({ conditions: d.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const setAct = (i: number, a: RuleAction) => set({ actions: d.actions.map((x, j) => (j === i ? a : x)) });
  const defaultRole = cat.roles.includes('manager') ? 'manager' : (cat.roles[0] ?? null);

  const save = () => {
    setTried(true);
    if (invalid) return;
    void run(async () => {
      await action('studio.save_automation', {
        ...(d.id ? { id: d.id } : {}),
        name: d.name.trim(),
        active: d.active ?? true,
        trigger: d.trigger,
        event: d.event,
        model: d.model,
        date_field: d.date_field,
        offset_days: d.offset_days,
        conditions: d.conditions,
        actions: d.actions,
      });
      onSaved();
    }, t('au.saved'));
  };
  const remove = () =>
    void run(async () => {
      await action('studio.delete_automation', { id: d.id });
      onSaved();
    }, t('au.deleted'));

  const triggerValue = d.trigger === 'event' ? (d.event ?? '') : `date:${d.model}|${d.date_field}`;
  const offsetDir = d.offset_days < 0 ? 'before' : d.offset_days > 0 ? 'after' : 'on';

  return (
    <Dialog
      size="lg"
      title={d.id ? t('au.edit_rule') : t('au.new_rule')}
      onClose={onClose}
      footer={
        <>
          {d.id ? (
            <Button variant="ghost" icon="trash" className="dialog-left" onClick={() => setDeleting(true)} disabled={busy}>
              {t('au.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={save} busy={busy}>
            {t('au.save_rule')}
          </Button>
        </>
      }
    >
      <Field label={t('au.name')} error={tried ? errors.name : null} required>
        <input className="input" maxLength={120} value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder={t('au.name_ph')} data-autofocus />
      </Field>

      <section className="au-step" aria-labelledby="au-when">
        <h3 className="au-step-head" id="au-when">
          <span className="au-step-n">1</span>
          {t('au.step_when')}
        </h3>
        <Field label={t('au.trigger')}>
          <select className="input" value={triggerValue} onChange={(e) => pickTrigger(e.target.value)}>
            <optgroup label={t('au.group_events')}>
              {cat.triggers.map((x) => (
                <option key={x.event} value={x.event}>
                  {t(EVENT_LABEL[x.event] ?? 'au.ev_unknown')}
                </option>
              ))}
            </optgroup>
            {cat.dates.length ? (
              <optgroup label={t('au.group_dates')}>
                {cat.dates.map((x) => (
                  <option key={`${x.model}|${x.field}`} value={`date:${x.model}|${x.field}`}>
                    {t(DATE_LABEL[`${x.model}.${x.field}`] ?? 'au.ev_unknown')}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </Field>
        {d.trigger === 'date' ? (
          <div className="au-offset">
            <Field label={t('au.days')}>
              <input
                className="input tabular"
                type="number"
                min={0}
                max={365}
                value={Math.abs(d.offset_days)}
                disabled={offsetDir === 'on'}
                onChange={(e) => {
                  const n = Math.min(365, Math.max(0, Math.round(Number(e.target.value) || 0)));
                  set({ offset_days: offsetDir === 'before' ? -n : n });
                }}
              />
            </Field>
            <Field label={t('au.relative')}>
              <select
                className="input"
                value={offsetDir}
                onChange={(e) => {
                  const n = Math.abs(d.offset_days) || 1;
                  set({ offset_days: e.target.value === 'on' ? 0 : e.target.value === 'before' ? -n : n });
                }}
              >
                <option value="before">{t('au.before')}</option>
                <option value="on">{t('au.on_day')}</option>
                <option value="after">{t('au.after')}</option>
              </select>
            </Field>
          </div>
        ) : null}
      </section>

      <section className="au-step" aria-labelledby="au-if">
        <h3 className="au-step-head" id="au-if">
          <span className="au-step-n">2</span>
          {t('au.step_if')}
          <span className="muted small">{t('au.optional')}</span>
        </h3>
        {d.conditions.map((c, i) => {
          const f = fields.find((x) => x.key === c.field);
          return (
            <div key={i} className="au-cond" role="group" aria-label={t('au.condition_n', { n: i + 1 })}>
              <select
                className="input"
                aria-label={t('au.field')}
                value={c.field}
                onChange={(e) => {
                  const nf = fields.find((x) => x.key === e.target.value)!;
                  setCond(i, { field: nf.key, op: opsFor(nf.type)[0]!, value: nf.type === 'bool' ? true : (nf.values?.[0] ?? null) });
                }}
              >
                {fields.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.label}
                  </option>
                ))}
              </select>
              <select className="input" aria-label={t('au.compare')} value={c.op} onChange={(e) => setCond(i, { op: e.target.value as Op })}>
                {opsFor(f?.type ?? 'string').map((o) => (
                  <option key={o} value={o}>
                    {t(OP_LABEL[o])}
                  </option>
                ))}
              </select>
              {c.op === 'set' || c.op === 'not_set' ? <span /> : <ValueInput field={f} value={c.value} people={cat.people} onChange={(value) => setCond(i, { value })} />}
              <IconButton icon="x" label={t('au.remove_condition')} onClick={() => set({ conditions: d.conditions.filter((_, j) => j !== i) })} />
            </div>
          );
        })}
        {fields.length && d.conditions.length < 10 ? (
          <div className="au-add">
            <Button
              size="sm"
              variant="ghost"
              icon="plus"
              onClick={() => {
                const f = fields[0]!;
                set({ conditions: [...d.conditions, { field: f.key, op: opsFor(f.type)[0]!, value: f.type === 'bool' ? true : (f.values?.[0] ?? null) }] });
              }}
            >
              {t('au.add_condition')}
            </Button>
          </div>
        ) : null}
      </section>

      <section className="au-step" aria-labelledby="au-do">
        <h3 className="au-step-head" id="au-do">
          <span className="au-step-n">3</span>
          {t('au.step_do')}
        </h3>
        {d.actions.map((a, i) => (
          <div key={i} className="au-action" role="group" aria-label={t('au.action_n', { n: i + 1 })}>
            <div className="au-action-head">
              <Icon name={a.kind === 'todo' ? 'checkSquare' : 'users'} size={16} />
              <span>{a.kind === 'todo' ? t('au.a_todo') : t('au.a_assign')}</span>
              <IconButton icon="x" label={t('au.remove_action')} onClick={() => set({ actions: d.actions.filter((_, j) => j !== i) })} />
            </div>
            {a.kind === 'todo' ? (
              <>
                <Field label={t('au.todo_title')} hint={t('au.todo_title_hint')}>
                  <input className="input" maxLength={200} value={a.title} onChange={(e) => setAct(i, { ...a, title: e.target.value })} />
                </Field>
                <div className="au-two">
                  <Field label={t('au.todo_for')}>
                    <select
                      className="input"
                      value={a.user ? `u:${a.user}` : `r:${a.role ?? ''}`}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAct(i, { ...a, user: v.startsWith('u:') ? v.slice(2) : null, role: v.startsWith('r:') ? v.slice(2) : null });
                      }}
                    >
                      <optgroup label={t('au.roles')}>
                        {cat.roles.map((r) => (
                          <option key={r} value={`r:${r}`}>
                            {t('au.everyone_role', { role: roleName(r) })}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label={t('au.people')}>
                        {cat.people.map((p) => (
                          <option key={p.id} value={`u:${p.id}`}>
                            {p.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </Field>
                  <Field label={t('au.todo_due')}>
                    <select className="input" value={a.due_days} onChange={(e) => setAct(i, { ...a, due_days: Number(e.target.value) })}>
                      {[0, 1, 2, 3, 5, 7, 14, 30].map((n) => (
                        <option key={n} value={n}>
                          {n === 0 ? t('au.due_same_day') : tn('au.due_in', n)}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </>
            ) : (
              <div className="au-two">
                <Field label={t('au.assign_field')}>
                  <select className="input" value={a.field} onChange={(e) => setAct(i, { ...a, field: e.target.value })}>
                    {assignable.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('au.assign_to')}>
                  <select className="input" value={a.user} onChange={(e) => setAct(i, { ...a, user: e.target.value })}>
                    <option value="">{t('au.choose_person')}</option>
                    {cat.people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
          </div>
        ))}
        {tried && (errors.actions || errors.todo) ? (
          <p className="field-msg" role="alert">
            <Icon name="alert" size={14} /> {errors.actions ?? errors.todo}
          </p>
        ) : null}
        {d.actions.length < 5 ? (
          <div className="au-add">
            {cat.todo ? (
              <Button
                size="sm"
                variant="ghost"
                icon="checkSquare"
                onClick={() => set({ actions: [...d.actions, { kind: 'todo', title: '', user: null, role: defaultRole, due_days: 0, notes: null }] })}
              >
                {t('au.add_todo')}
              </Button>
            ) : null}
            {assignable.length ? (
              <Button size="sm" variant="ghost" icon="users" onClick={() => set({ actions: [...d.actions, { kind: 'assign', field: assignable[0]!.key, user: '' }] })}>
                {t('au.add_assign')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>

      <p className="au-preview" aria-live="polite">
        <Icon name="repeat" size={16} />
        <span>
          <b>{t('au.when')}</b> {triggerText(d)}
          {d.conditions.length ? (
            <>
              {' '}
              <b>{t('au.and_if')}</b> {d.conditions.map((c) => conditionText(c, fields, cat.people)).join(` ${t('au.and')} `)}
            </>
          ) : null}
          {d.actions.length ? ` → ${d.actions.map((a) => actionText(a, cat, d.model)).join('; ')}` : ''}
        </span>
      </p>
      {deleting ? (
        <Confirm title={t('au.delete_title', { name: d.name })} body={t('au.delete_body')} confirm={t('au.delete')} danger busy={busy} onConfirm={remove} onClose={() => setDeleting(false)} />
      ) : null}
    </Dialog>
  );
}

/** The value box of a condition, shaped by the field: a list for choices, an amount for money, a date for dates. */
function ValueInput({ field, value, people, onChange }: { field: FieldInfo | undefined; value: Condition['value']; people: Catalog['people']; onChange: (v: Condition['value']) => void }) {
  const label = t('au.value');
  if (!field) return <span />;
  if (field.type === 'enum')
    return (
      <select className="input" aria-label={label} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
        {(field.values ?? []).map((v) => (
          <option key={v} value={v}>
            {enumText(v)}
          </option>
        ))}
      </select>
    );
  if (field.type === 'bool')
    return (
      <select className="input" aria-label={label} value={String(value === true || value === 'true')} onChange={(e) => onChange(e.target.value === 'true')}>
        <option value="true">{t('au.yes')}</option>
        <option value="false">{t('au.no')}</option>
      </select>
    );
  if (field.type === 'user')
    return (
      <select className="input" aria-label={label} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('au.choose_person')}</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    );
  if (field.type === 'money') return <MoneyInput value={typeof value === 'number' ? value : null} onChange={onChange} />;
  if (field.type === 'date' || field.type === 'datetime') return <input className="input" type="date" aria-label={label} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === 'int')
    return (
      <input className="input tabular" type="number" aria-label={label} value={value === null ? '' : String(value)} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
    );
  return <input className="input" aria-label={label} maxLength={200} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />;
}

function MoneyInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  const [text, setText] = useState(value === null ? '' : (value / 100).toFixed(2).replace(/\.00$/, ''));
  return (
    <input
      className="input tabular"
      inputMode="decimal"
      aria-label={t('au.value')}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseMoney(e.target.value));
      }}
    />
  );
}

function HistoryDialog({ rule, onClose }: { rule: Rule; onClose: () => void }) {
  const { data } = useAction<Run[]>('studio.automation_runs', { rule: rule.id });
  return (
    <Dialog title={t('au.history_title', { name: rule.name })} onClose={onClose}>
      {!data ? (
        <Skeleton h={160} />
      ) : !data.length ? (
        <EmptyState icon="history" title={t('au.history_empty')} body={t('au.history_empty_body')} />
      ) : (
        <ol className="au-history">
          {data.map((r) => (
            <li key={r.id} className={r.ok ? 'ok' : 'failed'}>
              <Icon name={r.ok ? 'checkCircle' : 'alert'} size={16} />
              <div>
                <div className="au-history-top">
                  <b>{r.label}</b>
                  <span className="muted small">{dateTime(r.at)}</span>
                </div>
                <span className="small">{r.ok ? r.done.join(' · ') || t('au.nothing_done') : r.error}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Dialog>
  );
}
